import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { useServiceRequestFile } from '../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';
import { EINDetailsModal } from './modal/EINDetailsModal';
import { UploadEINProofModal } from './modal/UploadEINProofModal';

export const EINProof = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.EINScreenshot
  );

  const uploadModalVisibility = useToggle();
  const einDetailsModalVisibility = useToggle();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={einDetailsModalVisibility.toggle}
              >
                View details
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="EIN proof uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads EIN proof" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads EIN proof" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={einDetailsModalVisibility.toggle}
              >
                View details
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="EIN proof uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.toggle}
                variant="primary"
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload EIN proof" />,
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload EIN proof" />,
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <UploadEINProofModal
        serviceRequest={serviceRequest}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        documentType={LegacyDocumentType.EINScreenshot}
        onUploadSuccess={() =>
          updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          })
        }
      />

      <EINDetailsModal
        serviceRequest={serviceRequest}
        isOpen={einDetailsModalVisibility.visible}
        setIsOpen={einDetailsModalVisibility.hide}
      />
    </div>
  );
};
